import { useCallback } from 'react';
import { useAuthStore } from './auth-store';

/**
 * Role → `perm_*` flag mapping, ported verbatim from the legacy admin
 * (Core/client/admin/src/backend/user_session.js). Keys must stay identical so
 * menu `requiredScope` values port across unchanged.
 */
const hasAccessMappings: Record<string, string> = {
	CMS: 'perm_cms',
	Catalog: 'perm_clg',
	Customers: 'perm_cust',
	Orders: 'perm_ord',
	OrderEntry: 'perm_oe',
	OrderApproval: 'perm_oa',
	Bookings: 'perm_bookings',
	System: 'perm_sys',
	Reports: 'perm_rep',
	Pricing: 'perm_prc',
	PromotionPolicies: 'perm_prompol',
	Dashboard: 'perm_dashboard'
};

/**
 * Returns a reactive `hasAccess(role)` predicate. Mirrors the legacy semantics:
 * no role → allowed; unknown role → denied; known role → the `perm_*` flag must be
 * exactly `'true'`. Reads from the session's `perms` map so it updates on login/out.
 */
export function useHasAccess(): (role?: string) => boolean {
	const perms = useAuthStore(s => s.session?.perms);
	return useCallback(
		(role?: string) => {
			if (!role) return true;
			const key = hasAccessMappings[role];
			if (!key) return false;
			return perms?.[key] === 'true';
		},
		[perms]
	);
}
